import type { Rng } from './rng';

/*
 * The world outside: the oil price and the economy, drifting from day to day. Dear oil means
 * dearer tickets (fewer passengers) and airlines cautious about new routes, but the fuel depot
 * sells for more. A strong economy fills aircraft and brings more airlines calling; a recession
 * does the opposite. Both drift slowly back towards normal, so a recession or a boom lasts
 * days, and oil has the odd shock. Crossing into a recession, a boom or an oil shock is news;
 * it is over only once well clear of the threshold again (flags, so the news is not repeated).
 */

/** Oil price, percent of normal (100 ≈ $80 a barrel), and the economy, -100 (deep recession) … 100 (boom). */
export const OIL_MIN = 50;
export const OIL_MAX = 200;
export const ECONOMY_LIMIT = 100;
/** A barrel at the normal price, in dollars (display). */
export const OIL_BARREL_DOLLARS = 80;
/** Chance (percent) a day of an oil shock: the price jumps (or slumps) by up to almost half. */
export const OIL_SHOCK_PERCENT = 3;

/** Oil price from which it is called a shock, and economy levels called a recession or a boom. */
export const OIL_HIGH = 140;
export const RECESSION = -45;
export const BOOM = 45;
/** How far back past a threshold before it is over (hysteresis). */
const RECOVERY = 15;

/** What the market is in right now (bits; saved). */
export const MarketFlag = { OilHigh: 1, Recession: 2, Boom: 4 } as const;

export interface MarketState {
  oilIndex: number;
  economy: number;
  marketFlags: number;
}

/** Market news worth telling the players. */
export type MarketChange = 'oilHigh' | 'oilNormal' | 'recession' | 'boom' | 'economyNormal';

/** Once a day (at midnight): both drift, pulled back towards normal. Returns what changed. */
export function updateMarket(m: MarketState, rng: Rng): MarketChange[] {
  let oil = m.oilIndex + Math.trunc((100 - m.oilIndex) / 15) + rng.range(-7, 7);
  if (rng.int(100) < OIL_SHOCK_PERCENT) oil += Math.trunc((oil * rng.range(-25, 45)) / 100);
  m.oilIndex = Math.max(OIL_MIN, Math.min(OIL_MAX, oil));
  const economy = m.economy - Math.trunc(m.economy / 33) + rng.range(-14, 14);
  m.economy = Math.max(-ECONOMY_LIMIT, Math.min(ECONOMY_LIMIT, economy));

  const changes: MarketChange[] = [];
  const has = (flag: number) => (m.marketFlags & flag) !== 0;
  if (!has(MarketFlag.OilHigh) && m.oilIndex >= OIL_HIGH) {
    m.marketFlags |= MarketFlag.OilHigh;
    changes.push('oilHigh');
  } else if (has(MarketFlag.OilHigh) && m.oilIndex < OIL_HIGH - RECOVERY) {
    m.marketFlags &= ~MarketFlag.OilHigh;
    changes.push('oilNormal');
  }
  if (!has(MarketFlag.Recession) && m.economy <= RECESSION) {
    m.marketFlags = (m.marketFlags | MarketFlag.Recession) & ~MarketFlag.Boom;
    changes.push('recession');
  } else if (!has(MarketFlag.Boom) && m.economy >= BOOM) {
    m.marketFlags = (m.marketFlags | MarketFlag.Boom) & ~MarketFlag.Recession;
    changes.push('boom');
  } else if (
    (has(MarketFlag.Recession) && m.economy > RECESSION + RECOVERY) ||
    (has(MarketFlag.Boom) && m.economy < BOOM - RECOVERY)
  ) {
    m.marketFlags &= ~(MarketFlag.Recession | MarketFlag.Boom);
    changes.push('economyNormal');
  }
  return changes;
}

/** How often airlines call with new routes (percent of normal). */
export function marketOffers(m: Pick<MarketState, 'oilIndex' | 'economy'>): number {
  return 100 + Math.trunc(m.economy / 3) - Math.trunc(Math.max(0, m.oilIndex - 100) / 4);
}

/** Load factor shift (percentage points): demand follows the economy and ticket prices. */
export function marketLoad(m: Pick<MarketState, 'oilIndex' | 'economy'>): number {
  return Math.trunc(m.economy / 8) - Math.trunc(Math.max(0, m.oilIndex - 120) / 10);
}

/** Fuel sales of the depot follow the price of oil (percent of normal). */
export function marketFuel(m: Pick<MarketState, 'oilIndex'>): number {
  return 50 + Math.trunc(m.oilIndex / 2);
}

/** Oil price in dollars a barrel (display). */
export function barrelPrice(oilIndex: number): number {
  return Math.floor((oilIndex * OIL_BARREL_DOLLARS + 50) / 100);
}
